import { useCollectors, useTriggerSync } from "../hooks/useCollectors";
import PageHeader from "../components/common/PageHeader";
import StatusBadge from "../components/common/StatusBadge";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import { format, formatDistanceToNow } from "date-fns";

// API returns snake_case fields
interface CollectorData {
  collector_name: string;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_error: string | null;
  items_synced: number;
  next_sync_at: string | null;
  is_healthy: boolean;
  schedule: string | null;
}

const collectorDescriptions: Record<string, string> = {
  github: "Repositories, workflows, PRs, and commits from GitHub API",
  datadog: "Monitors, metrics, and alerts from Datadog",
  pulumi: "Infrastructure stacks and resources from Pulumi Cloud",
  costs: "Cloud billing data from AWS, Azure, and GCP",
};

export default function CollectorStatus() {
  const {
    data: collectors,
    isLoading,
    error,
    refetch,
  } = useCollectors();
  const triggerSync = useTriggerSync();

  const handleSync = (collectorName: string) => {
    triggerSync.mutate(
      { collectorName, force: false },
      { onSuccess: () => refetch() }
    );
  };

  if (isLoading)
    return <LoadingSpinner message="Loading collector status..." />;
  if (error)
    return (
      <ErrorBanner
        message="Failed to load collector status"
        onRetry={refetch}
      />
    );

  const items = (collectors ?? []) as CollectorData[];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Collector Status"
        description="Monitor and manage data collection from external systems"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {items.map((collector) => (
          <div
            key={collector.collector_name}
            className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 shadow-card hover:shadow-card-hover hover:border-slate-200 dark:hover:border-slate-700 transition-all overflow-hidden"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                    collector.is_healthy ? "bg-emerald-500 dark:bg-emerald-600" : "bg-red-500 dark:bg-red-600"
                  }`}
                >
                  {collector.collector_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 capitalize">
                    {collector.collector_name}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {collectorDescriptions[collector.collector_name] ??
                      "Data collector"}
                  </p>
                </div>
              </div>
              <StatusBadge
                status={collector.is_healthy ? "green" : "red"}
                label={collector.is_healthy ? "Healthy" : "Unhealthy"}
              />
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Last Sync</span>
                <span className="text-slate-900 dark:text-slate-200 font-medium">
                  {collector.last_sync_at
                    ? formatDistanceToNow(new Date(collector.last_sync_at), {
                        addSuffix: true,
                      })
                    : "Never"}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Status</span>
                <span
                  className={`font-medium ${
                    collector.last_sync_status === "success"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : collector.last_sync_status === "failed"
                        ? "text-red-600 dark:text-red-400"
                        : "text-slate-600 dark:text-slate-300"
                  }`}
                >
                  {collector.last_sync_status ?? "—"}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Items Synced</span>
                <span className="text-slate-900 dark:text-slate-200 font-medium">
                  {collector.items_synced.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Schedule</span>
                <span className="text-slate-600 dark:text-slate-300 font-mono text-xs">
                  {collector.schedule ?? "—"}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Next Sync</span>
                <span className="text-slate-600 dark:text-slate-300">
                  {collector.next_sync_at
                    ? format(new Date(collector.next_sync_at), "HH:mm:ss")
                    : "—"}
                </span>
              </div>

              {collector.last_error && (
                <div className="mt-2 p-3 bg-red-50 dark:bg-rose-950/30 rounded-md border border-red-100 dark:border-rose-900/40">
                  <p className="text-xs text-red-700 dark:text-red-300 font-mono truncate">
                    {collector.last_error}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-800 bg-surface-50 dark:bg-slate-950/60">
              <button
                onClick={() => handleSync(collector.collector_name)}
                disabled={triggerSync.isPending}
                className="w-full px-4 py-2 text-sm font-medium text-brand-600 bg-white border border-brand-200 rounded-lg hover:bg-brand-50 dark:text-brand-400 dark:bg-slate-800 dark:border-brand-800 dark:hover:bg-brand-950/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {triggerSync.isPending ? "Triggering..." : "Sync Now"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500">
          <p className="text-lg">No collectors configured</p>
          <p className="text-sm mt-1">
            Configure collectors in the backend settings
          </p>
        </div>
      )}
    </div>
  );
}
